import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import VehicleImage from '@/components/vehicles/vehicle-image'

interface GalleryImage {
    src: string | null
    alt: string
}

interface VehicleGalleryProps {
    images: Array<GalleryImage>
    categoria: string
    name: string
}

const arrowClass = 'absolute top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow-md backdrop-blur transition hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

export default function VehicleGallery({ images, categoria, name }: VehicleGalleryProps) {
    const [active, setActive] = useState(0)
    const slides = images.length > 0 ? images : [{ src: null, alt: name }]
    const index = Math.min(active, slides.length - 1)
    const current = slides[index]
    const hasMany = slides.length > 1

    const go = (direction: 1 | -1) => setActive(previous => (previous + direction + slides.length) % slides.length)

    return (
        <div>
            <div className='relative aspect-[16/10] overflow-hidden rounded-3xl border border-border/70 bg-muted shadow-[0_30px_60px_-40px_rgb(40_30_10/0.5)]'>
                <VehicleImage
                    key={current.src ?? 'sin-foto'}
                    src={current.src}
                    alt={current.alt}
                    categoria={categoria}
                    sizes='(min-width: 1280px) 720px, (min-width: 1024px) 58vw, 100vw'
                    preload={index === 0}
                />

                {hasMany && (
                    <>
                        <button type='button' onClick={() => go(-1)} aria-label='Foto anterior' className={cn(arrowClass, 'left-4')}>
                            <ChevronLeft className='size-5' aria-hidden='true' />
                        </button>

                        <button type='button' onClick={() => go(1)} aria-label='Foto siguiente' className={cn(arrowClass, 'right-4')}>
                            <ChevronRight className='size-5' aria-hidden='true' />
                        </button>

                        <span aria-live='polite' className='absolute right-4 bottom-4 rounded-full bg-foreground/75 px-3 py-1 text-xs font-medium text-background tabular-nums backdrop-blur'>
                            {index + 1} / {slides.length}
                        </span>
                    </>
                )}
            </div>

            {hasMany && (
                <ul className='mt-4 grid grid-cols-4 gap-3 sm:grid-cols-5'>
                    {slides.map((image, slideIndex) => (
                        <li key={`${image.src ?? 'sin-foto'}-${slideIndex}`}>
                            <button
                                type='button'
                                onClick={() => setActive(slideIndex)}
                                aria-label={`Ver foto ${slideIndex + 1} de ${slides.length}`}
                                aria-pressed={slideIndex === index}
                                className={cn(
                                    'relative block aspect-[4/3] w-full overflow-hidden rounded-xl border-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    slideIndex === index ? 'border-gold' : 'border-transparent opacity-70 hover:opacity-100'
                                )}
                            >
                                <VehicleImage src={image.src} alt='' categoria={categoria} sizes='160px' />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
